/**
 * AdditionsConfigurationSection component for the dish form
 */

import React, { useRef } from 'react';
import GroupSelector from '../comman/GroupSelector.js';
import AdditionsSection from '../comman/AdditionsSection.js';
import { getTranslation } from '../../utils/translate.js';

/**
 * Spacing constants for consistent layout
 * Using 8px increments (0.5rem = 8px in Tailwind)
 */
const SPACING = {
  // Typography and section styles
  SECTION_TITLE: 'text-xl font-bold border-b pb-2 mb-4 text-gray-800 dark:text-[#c9d1d9] border-gray-200 dark:border-[#30363d]',
  SECTION_HEADER: 'flex justify-between items-center border-b pb-2 mb-4',

  // Group styles
  GROUP_CONTAINER: 'mb-6 p-3 sm:p-4 bg-white dark:bg-[#0d1117] rounded-lg border border-gray-200 dark:border-[#30363d]',
  GROUP_TITLE: 'text-md font-medium text-gray-700 dark:text-[#b1bac4] mb-2',
  GROUP_DESCRIPTION: 'text-sm text-gray-600 dark:text-[#8b949e] mb-3',

  // Additions styles
  ADDITIONS_CONTAINER: 'space-y-4',
  SELECTOR_CONTAINER: 'mt-2',
};

/**
 * AdditionsConfigurationSection component
 * @param {Object} props - Component props
 * @returns {JSX.Element|null} AdditionsConfigurationSection component or null if no groups
 */
const AdditionsConfigurationSection = ({
  additionGroups,
  groupedAdditionsMap,
  configuredGroupIds,
  availableGroupsForConfig,
  formData,
  errors,
  handleAddGroupConfiguration,
  handleGroupedAdditionChange,
  handleRemoveGroupConfiguration,
  handleAdditionPriceOverrideChange,
  handleCreateNewAddition,
  language
}) => {
  // This is a reference for scrolling to the configuration section
  const configSectionRef = useRef(null);
  const additionsSectionRef = useRef(null);

  // If there are no groups or no additions in groups, don't render this section
  if (!additionGroups.length || !groupedAdditionsMap.size) {
    return null;
  }

  return (
    <div ref={configSectionRef}>
      <div className={SPACING.SECTION_HEADER}>
        <h2 className={SPACING.SECTION_TITLE}>
          {getTranslation('menu.configuration', language)}
        </h2>
      </div>

      {/* Group Selection - Mobile friendly */}
      <div className={SPACING.GROUP_CONTAINER}>
        <h3 className={SPACING.GROUP_TITLE}>
          {getTranslation('menu.selectConfigGroup', language) || 'Select an addition group'}
        </h3>
        <p className={SPACING.GROUP_DESCRIPTION}>
          {getTranslation('menu.addGroupDescription', language) || 'Choose an additions group you want to add to this dish'}
        </p>
        <div className={SPACING.SELECTOR_CONTAINER}>
          <GroupSelector
            availableGroups={availableGroupsForConfig}
            onGroupSelect={handleAddGroupConfiguration}
            language={language}
          />
        </div>
      </div>

      {/* Configure Additions - Only show when groups are selected */}
      {configuredGroupIds.length > 0 && (
        <div className={SPACING.GROUP_CONTAINER} ref={additionsSectionRef}>
          <h3 className={SPACING.GROUP_TITLE}>
            {getTranslation('menu.configureGroupAdditions', language) || 'Configure additions'}
          </h3>
          <p className={SPACING.GROUP_DESCRIPTION}>
            {getTranslation('menu.configureAdditionsDescription', language) || 'Select desired additions and optionally change their prices'}
          </p>
          <div className={SPACING.ADDITIONS_CONTAINER}>
            {configuredGroupIds.map(groupId => {
              const group = additionGroups.find(g => g.id === groupId);
              if (!group) return null;

              const groupAdditions = groupedAdditionsMap.get(groupId) || [];
              const selectedAdditions = formData.dishAdditionDtoList.filter(add => add.group?.id === groupId);

              return (
                <AdditionsSection
                  key={groupId}
                  group={group}
                  groupAdditions={groupAdditions}
                  selectedAdditions={selectedAdditions}
                  onAdditionsChange={handleGroupedAdditionChange}
                  onRemoveGroup={handleRemoveGroupConfiguration}
                  onPriceOverrideChange={handleAdditionPriceOverrideChange}
                  onCreateNewAddition={handleCreateNewAddition}
                  errors={errors}
                  language={language}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdditionsConfigurationSection;
